<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 full-height-page-wrapper">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs :page-title="'Sales channels'">
            <template v-slot:buttons>
              <b-button
                v-if="$can('create-finances_sales_channels')"
                variant="success"
                class="button"
                @click.prevent="editEntityModalOpen()"
                :disabled="isLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>
        </div>

        <div
          v-if="isLoading"
          class="content-loading-wrapper"
          style="min-height: 60vh"
        >
          <Preloader class="centered-preloader large-spinner" />
        </div>

        <TableForPage
          v-else-if="salesChannelsTable.length"
          class="table__sales-channels"
          :table-data="salesChannelsTable"
          :fields="fields"
          :slots="{
            comment: { name: 'comment' },
            is_default: { name: 'is_default' },
          }"
          :isRemove="$can('delete_own-finances_sales_channels')"
          :isEdit="$can('update_own-finances_sales_channels')"
          @edit="editEntityModalOpen"
          @remove="removeSalesChannel"
        >
          <template v-slot:comment="slotProps">
            <TextEditor
              :show-text="true"
              empty-text=""
              data-already-loaded
              :entity-type="commentEntityTypes.SalesChannelItem"
              :entity-id="slotProps.item ? slotProps.item.id : null"
            />
          </template>
          <template v-slot:is_default="slotProps">
            <span v-if="slotProps.item.is_default" style="color: #3fa52f"
              >&check;</span
            >
          </template>
        </TableForPage>

        <div v-else class="full-height-content">
          <div class="full-height-content">
            <EmptyStateNoResults
              customIcon="bar_chart"
              customIconClasses="material-symbols-outlined empty-state__icon"
              title="No sales channels added yet"
              description="Connect or create sales channels to start tracking and managing your revenue sources."
              buttonText="CREATE"
              @button-click="handleCreateClick"
            />
          </div>
        </div>
        <b-modal
          size="md"
          hide-footer
          v-model="isOpenDialog"
          class="modal"
          centered
          :modal-class="isModalLoading ? 'loading' : ''"
        >
          <template v-slot:modal-header="{ cancel }">
            <h5 class="modal-title">
              {{
                !!editedItem.id ? "Edit sales channel" : "Create sales channel"
              }}
            </h5>
            <button
              type="button"
              aria-label="Close"
              class="close"
              @click.prevent="cancel"
            >
              <span class="material-icons"> close </span>
            </button>
          </template>
          <FormGenerator
            :data="form"
            :submitButtonText="!!editedItem.id ? 'Save' : 'Create'"
            :showReset="false"
            :isLoading="isModalLoading"
            @submit="onSubmit"
          />
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { permissions } from '@mocks/permissions'
import { commentEntityTypes } from '@mocks/comment-entity-types'
import PageHeader from '@components/PageHeader'
import FormGenerator from '@components/FormGenerator'
import TextEditor from '@components/TextEditor.vue'
import Preloader from '@components/Preloader'
import TableForPage from '@tables/TableForPage'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  components: {
    PageHeader,
    FormGenerator,
    TextEditor,
    Preloader,
    TableForPage,
    EmptyStateNoResults
  },
  data () {
    const commentField = this.$can(permissions.ReadAllComments)
      ? [
        {
          key: 'comment',
          label: 'Comment'
        }
      ]
      : []
    return {
      permissions,
      isLoading: true,
      isModalLoading: false,
      modalTitle: 'sales channel',
      isOpenDialog: false,
      editedItem: {},
      fields: [
        {
          key: 'name',
          label: 'Name'
        },
        ...commentField,
        {
          key: 'is_default',
          label: 'Default'
        },
        {
          key: 'actions',
          label: 'Actions',
          class: 'column-controls-md'
        }
      ],
      form: {
        id: {
          name: 'id'
        },
        name: {
          type: 'input',
          name: 'name',
          label: 'Name*',
          placeholder: 'Enter name',
          valueField: 'id',
          selectLabel: 'name',
          value: '',
          validation: [{ name: 'required' }],
          class: 'col-12'
        },
        is_default: {
          type: 'checkbox',
          name: 'is_default',
          label: 'Default',
          value: false,
          class: 'col-12'
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      salesChannels: 'finances/getSalesChannels',
      getComment: 'comments/getComment'
    }),
    salesChannelsTable () {
      if (!this.salesChannels || !Array.isArray(this.salesChannels)) {
        return []
      }
      return this.salesChannels.map((item) => {
        item.comment =
          this.getComment(commentEntityTypes.SalesChannelItem, item.id)
            .comment ?? ''
        return item
      })
    },
    commentEntityTypes () {
      return commentEntityTypes
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments',
      setComment: 'comments/setComment'
    }),
    editEntityModalOpen (item) {
      if (item) {
        this.editedItem = item
        this.form.id.value = this.editedItem.id
        this.form.name.value = this.editedItem.name
        this.form.is_default.value = this.editedItem.is_default
      } else {
        this.editedItem = {}
        this.form.id.value = null
        this.form.name.value = null
        this.form.is_default.value = null
      }

      this.isOpenDialog = true
    },
    onSubmit (form) {
      const data = {
        name: form.name.value,
        is_default: !!form.is_default.value
      }
      if (this.editedItem.id) {
        this.editSalesChannel({ ...data, id: this.editedItem.id })
      } else {
        this.createSalesChannel(data)
      }
    },
    createSalesChannel (data) {
      this.isModalLoading = true

      this.axios
        .post('/api/finances/sales-channels', data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$api.finances.getSalesChannels()

          this.$snotify.success(
            'Sales channel successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.isOpenDialog = false
          this.editedItem = {}
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    editSalesChannel (data) {
      this.isModalLoading = true

      this.axios
        .put(`/api/finances/sales-channels/${this.editedItem.id}`, data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$api.finances.getSalesChannels()

          this.$snotify.success(
            'Sales channel successfully updated',
            'Success',
            this.$store.state.snotify
          )

          this.isOpenDialog = false
          this.editedItem = {}
          this.showModal = false
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    async removeSalesChannel (item) {
      this.isLoading = true

      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete?'
      )

      if (!confirm) {
        this.isLoading = false
        return
      }

      this.axios
        .delete(`/api/finances/sales-channels/${item.id}`, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$api.finances.getSalesChannels()

          this.$snotify.success(
            'Sales channel successfully deleted',
            'Success',
            this.$store.state.snotify
          )

          this.editedItem = {}
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleCreateClick () {
      this.editEntityModalOpen()
    }
  },
  mounted () {
    this.isLoading = true
    this._toggleDataPageLoading(async () => {
      if (this.$can(permissions.ReadAllComments)) {
        await this.fetchComments({ type: commentEntityTypes.SalesChannelItem })
      }
      await this.$api.finances.getSalesChannels()
      this.isLoading = false
    })
  }
}
</script>

<style lang="scss" scoped>
.table__sales-channels {
  &.table__theme-default {
    ::v-deep.column-controls-md {
      width: 65px;
    }
  }
}
</style>
